/**
 * Media fetching for images and assets.
 * @module api/media
 */

import { BACKEND, fetchWithRetry, getAuthHeaders } from "./client";
import { arrayBufferToBase64 } from "../utils/helpers";
import { checkCancel, getAbortSignal } from "../state";

/**
 * Validates a local media path against path traversal attacks.
 * Rejects paths containing ".." or characters outside [a-zA-Z0-9._/-].
 * @param {string} path
 * @returns {boolean}
 */
function isSafeLocalPath(path) {
  if (!path) return false;
  if (path.includes("..")) return false;
  return /^[a-zA-Z0-9._\/-]+$/.test(path);
}

/**
 * Pre-fetches all images in the block tree as base64 data URIs.
 * @param {Array<object>} blocks - Notion block tree
 * @param {function} [onProgress] - Called with (current, total) after each image
 * @returns {Promise<object>} Cache mapping block ID → data URI string
 */
export async function prefetchImages(blocks, onProgress) {
  const cache = {};

  const imageBlocks = [];
  function collect(bs) {
    for (const b of bs || []) {
      if (b.type === "image") imageBlocks.push(b);
      if (b.children) collect(b.children);
    }
  }
  collect(blocks);

  for (let i = 0; i < imageBlocks.length; i++) {
    checkCancel();
    if (onProgress) onProgress(i + 1, imageBlocks.length);
    const b = imageBlocks[i];
    const localPath = b.local_path ? b.local_path.replace(/\\/g, "/") : null;
    const src = (localPath && isSafeLocalPath(localPath))
      ? `${BACKEND}/api/media/${localPath}`
      : b.url;
    if (src) {
      try {
        const opts = b.local_path ? { headers: getAuthHeaders() } : {};
        const signal = getAbortSignal();
        if (signal) opts.signal = signal;
        const r = await fetchWithRetry(src, opts);
        if (r.ok) {
          const mime = r.headers.get("content-type") || "image/png";
          const buf = await r.arrayBuffer();
          cache[b.id] = `data:${mime};base64,${arrayBufferToBase64(buf)}`;
        }
      } catch (err) {
        // AbortError = annulation → propager pour arrêter la boucle
        if (err.name === "AbortError") throw err;
        /* sinon image indisponible, on l'ignore */
      }
    }
  }

  return cache;
}

/**
 * Fetches a local asset file and returns its base64 representation.
 * @param {string} relativePath - Path relative to the webpack output (e.g. "assets/sbi-logo.png")
 * @returns {Promise<string>} Base64 string (without data URI prefix)
 */
export async function fetchImageBase64(relativePath) {
  const resp = await fetch(relativePath);
  if (!resp.ok) throw new Error(`Impossible de charger ${relativePath}`);
  const buf = await resp.arrayBuffer();
  return arrayBufferToBase64(buf);
}
